import { ChevronDown, type LucideIcon } from 'lucide-react';
import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { Button, type ButtonVariant } from './Button';

export interface MenuItem {
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  danger?: boolean;
  hidden?: boolean;
  disabled?: boolean;
}

/** A button that opens a list of actions (arrow keys, Escape and outside clicks work). */
export function ActionMenu({ label, items, variant = 'secondary', icon }: { label: string; items: MenuItem[]; variant?: ButtonVariant; icon?: LucideIcon }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const visible = items.filter((i) => !i.hidden);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    rootRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus();
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      rootRef.current?.querySelector<HTMLElement>('[aria-haspopup]')?.focus();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const list = Array.from(rootRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? []);
    const i = list.indexOf(document.activeElement as HTMLElement);
    list[(i + (e.key === 'ArrowDown' ? 1 : -1) + list.length) % list.length]?.focus();
  };

  if (!visible.length) return null;
  return (
    <div ref={rootRef} className="relative" onKeyDown={onKeyDown}>
      <Button variant={variant} icon={icon} iconEnd={ChevronDown} aria-haspopup="menu" aria-expanded={open} aria-controls={menuId} onClick={() => setOpen((o) => !o)}>
        {label}
      </Button>
      {open ? (
        <div id={menuId} role="menu" className="absolute end-0 top-full z-30 mt-2 min-w-60 rounded-lg border border-line bg-surface p-1.5 shadow-pop">
          {visible.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
                className={cn(
                  'flex h-11 w-full items-center gap-3 rounded-md px-3 text-start text-base whitespace-nowrap hover:bg-hover focus-visible:bg-hover disabled:opacity-50',
                  item.danger && 'text-danger-text',
                )}
              >
                {Icon ? <Icon aria-hidden className={cn('size-[18px]', item.danger ? 'text-danger-text' : 'text-subtle')} /> : null}
                {item.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
